import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowRightLeft,
  X,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Building,
  BookOpen,
  Send,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTransferCart } from "@/contexts/TransferCartContext";
import { useToast } from "@/contexts/ToastContext";
import { itemTransfersApi } from "@/lib/api/item-transfers";
import { Branch } from "@/lib/api/branches";
import { cn } from "@/lib/utils";

interface TransferQueueDrawerProps {
  open: boolean;
  onClose: () => void;
  branches: Branch[];
}

export function TransferQueueDrawer({ open, onClose, branches }: TransferQueueDrawerProps) {
  const { items, removeItem, updateItem, clearCart, count } = useTransferCart();
  const toast = useToast();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [bulkBranchId, setBulkBranchId] = useState("");
  const [submitted, setSubmitted] = useState<{ succeeded: number; failed: number } | null>(null);

  const toggleNotes = (itemId: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const applyBulkBranch = () => {
    if (!bulkBranchId) return;
    const branch = branches.find((b) => b.id === bulkBranchId);
    if (!branch) return;
    items.forEach((item) =>
      updateItem(item.itemId, { toBranchId: bulkBranchId, toBranchName: branch.name })
    );
  };

  const allHaveDestination = items.every((i) => !!i.toBranchId);
  const readyCount = items.filter((i) => !!i.toBranchId).length;

  const handleSubmit = async () => {
    if (!allHaveDestination) {
      toast.error("Please set a destination branch for every item before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      const result = await itemTransfersApi.createBulk(
        items.map((item) => ({
          itemId: item.itemId,
          fromBranchId: item.currentBranchId || undefined,
          toBranchId: item.toBranchId,
          notes: item.notes || undefined,
        }))
      );

      setSubmitted({ succeeded: result.succeeded, failed: result.failed });

      if (result.succeeded > 0) {
        // Remove successfully submitted items from cart
        result.results.filter((r) => r.success).forEach((r) => removeItem(r.itemId));
        toast.success(
          `${result.succeeded} transfer request${result.succeeded !== 1 ? "s" : ""} submitted!`
        );
      }
      if (result.failed > 0) {
        const errorMessages = result.results
          .filter((r) => !r.success)
          .map((r) => r.error)
          .join("; ");
        toast.error(
          `${result.failed} request${result.failed !== 1 ? "s" : ""} failed: ${errorMessages}`
        );
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to submit transfer requests");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Transfer Queue</h2>
              <p className="text-xs text-gray-500">
                {count === 0 ? "No items" : `${count} item${count !== 1 ? "s" : ""} queued`}
                {count > 0 && !allHaveDestination && (
                  <span className="text-amber-600 ml-1">
                    · {count - readyCount} need destination
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Bulk Branch Setter */}
        {count > 0 && (
          <div className="px-5 py-3 bg-primary/5 border-b border-primary/10 flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs font-medium text-primary mb-1 block">
                Set destination for all items:
              </Label>
              <Select value={bulkBranchId} onValueChange={setBulkBranchId}>
                <SelectTrigger className="h-8 text-sm bg-white">
                  <SelectValue placeholder="Select branch..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 bg-white"
              onClick={applyBulkBranch}
              disabled={!bulkBranchId}
            >
              Apply to All
            </Button>
          </div>
        )}

        {/* Items List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {count === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <ArrowRightLeft className="w-12 h-12 text-gray-200 mb-3" />
              <p className="text-gray-500 font-medium">Your transfer queue is empty</p>
              <p className="text-xs text-gray-400 mt-1">
                Browse the catalog and click "Add to Transfer Queue" on any item copy
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => {
                  onClose();
                  navigate("/catalog");
                }}
              >
                Browse Catalog
              </Button>
            </div>
          ) : (
            items.map((item) => {
              const hasDestination = !!item.toBranchId;
              const notesExpanded = expandedNotes.has(item.itemId);

              return (
                <div
                  key={item.itemId}
                  className={cn(
                    "rounded-lg border bg-white p-3 space-y-2",
                    hasDestination ? "border-gray-200" : "border-amber-300 bg-amber-50/30"
                  )}
                >
                  {/* Item info row */}
                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                      <BookOpen className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.biblioTitle}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">
                        Copy #{item.copyNumber} · {item.barcode}
                      </p>
                      {item.currentBranchName && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <Building className="w-3 h-3" />
                          From: {item.currentBranchName}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0 text-gray-400 hover:text-red-500"
                      onClick={() => removeItem(item.itemId)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Destination selector */}
                  <div className="flex items-center gap-2">
                    {!hasDestination && (
                      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    )}
                    {hasDestination && (
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    )}
                    <Select
                      value={item.toBranchId || "none"}
                      onValueChange={(val) => {
                        if (val === "none") return;
                        const branch = branches.find((b) => b.id === val);
                        updateItem(item.itemId, {
                          toBranchId: val,
                          toBranchName: branch?.name || "",
                        });
                      }}
                    >
                      <SelectTrigger
                        className={cn("h-8 text-sm flex-1", !hasDestination && "border-amber-300")}
                      >
                        <SelectValue placeholder="Select destination branch..." />
                      </SelectTrigger>
                      <SelectContent>
                        {branches
                          .filter((b) => b.id !== item.currentBranchId)
                          .map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Notes toggle */}
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                    onClick={() => toggleNotes(item.itemId)}
                  >
                    {notesExpanded ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                    {item.notes
                      ? `Notes: "${item.notes.slice(0, 40)}${item.notes.length > 40 ? "…" : ""}"`
                      : "Add notes (optional)"}
                  </button>

                  {notesExpanded && (
                    <Textarea
                      value={item.notes || ""}
                      onChange={(e) => updateItem(item.itemId, { notes: e.target.value })}
                      placeholder="Reason for transfer, urgency..."
                      className="h-16 text-sm resize-none"
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        {count > 0 && (
          <div className="border-t border-gray-200 px-5 py-4 bg-gray-50 space-y-3">
            {!allHaveDestination && (
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>
                  {count - readyCount} item{count - readyCount !== 1 ? "s are" : " is"} missing a
                  destination branch.
                </span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  clearCart();
                  setSubmitted(null);
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear All
              </Button>
              <Button
                className="flex-1 gap-2"
                disabled={!allHaveDestination || submitting || count === 0}
                onClick={handleSubmit}
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                    Submitting {count} request{count !== 1 ? "s" : ""}...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit {count} Transfer Request{count !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            </div>

            {submitted && (
              <p className="text-xs text-center text-gray-500">
                Last batch: {submitted.succeeded} succeeded
                {submitted.failed > 0 && `, ${submitted.failed} failed`}
                {" · "}
                <button
                  className="text-primary hover:underline"
                  onClick={() => {
                    onClose();
                    navigate("/setup/transfers");
                  }}
                >
                  View all transfers
                </button>
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
